import {ActionList, AnchoredOverlay, NavList, useMergedRefs} from '@primer/react'
import {FeatureFlags} from '@primer/react/experimental'
import {Link} from '@github-ui/react-router'
import {clsx} from 'clsx'
import {Children, Fragment, isValidElement, useId, useLayoutEffect, useMemo, useRef, useState} from 'react'
import type {ComponentProps, ComponentType, HTMLAttributes, MouseEvent, ReactNode, Ref} from 'react'

import {CollapsibleSidebarContext, useCollapsibleSidebarContext} from '../CollapsibleSidebar/CollapsibleSidebar'
import styles from './CollapsibleNavList.module.css'

/**
 * `CollapsibleNavList` is a thin wrapper around Primer's `NavList` that
 * adapts its subcomponents to a surrounding `<CollapsibleSidebar>` (or any
 * provider of `CollapsibleSidebarContext`):
 *
 * - `LinkItem` — the high-level primitive most consumers want. Takes
 *   primitive `icon` + `label` props (and routing props). Renders the
 *   visible label when expanded and a `SidebarTooltip` over the icon
 *   when collapsed. Handles the soft-nav vs hard-nav switch in one
 *   component.
 *
 * - `Item` — a low-level escape hatch over Primer's `NavList.Item`. Use it
 *   when you need the full Primer `Item` API (badges, custom slot
 *   composition, polymorphic `as`, etc.). When the sidebar is collapsed, an
 *   item with no `LeadingVisual` is hidden from the rail — it would otherwise
 *   render as a blank, unlabelled row, since the rail clips text labels. Give
 *   it a `LeadingVisual` icon to keep it visible when collapsed.
 *
 * - `Group` — re-exports Primer's `NavList.Group` but auto-suppresses
 *   the group title when collapsed so empty heading rows don't appear in
 *   the icon-only rail. The underlying `<ul>` stays so AT preserves the
 *   grouping semantics.
 *
 * - `GroupHeading` — re-exports Primer's slot, hiding it visually when
 *   collapsed while preserving it as the nested list's accessible label.
 *
 * Other slot exports (`LeadingVisual`, `TrailingVisual`, etc.) are direct
 * pass-throughs of Primer's slot components so Primer's slot-detection
 * (`__SLOT__` symbol comparisons) keeps working.
 */

// ---------------------------------------------------------------------------
// LinkItem — the high-level primitive

export type CollapsibleNavListLinkItemProps = Omit<ComponentProps<typeof NavList.Item>, 'href' | 'as' | 'children'> & {
  /**
   * Path to navigate to. Used as the router `Link`'s `to` prop for soft
   * navigation, or as the `<a>` element's `href` when
   * `forceHardNavigation` is set.
   */
  to: string
  /** Visible text label. Also used as the tooltip text when collapsed. */
  label: string
  /**
   * Icon component rendered inside `NavList.LeadingVisual`. Required
   * because the icon is the only visible affordance in the icon-only
   * rail when the surrounding sidebar is collapsed.
   */
  icon: ComponentType<{size?: number; className?: string}>
  /**
   * Arbitrary content rendered inside `NavList.TrailingVisual` while expanded.
   * Use this for icons, counters, and badges.
   */
  trailingVisual?: ReactNode
  /**
   * Render as a plain `<a href={to}>` (full page load) instead of using
   * navigation through `Link` from `@github-ui/react-router`. Set
   * this for routes that aren't owned by the current router (e.g. legacy
   * Rails pages, cross-app navigation). Note that `Link` already
   * hard-navigates when a route isn't registered, so this is only needed
   * to force a full load for routes the current router *could* match.
   */
  forceHardNavigation?: boolean
}

function LinkItem({
  to,
  label,
  icon: Icon,
  forceHardNavigation,
  trailingVisual,
  ...rest
}: CollapsibleNavListLinkItemProps) {
  const {visualIsExpanded} = useCollapsibleSidebarContext()
  const resolvedTrailingVisual = trailingVisual !== undefined ? (visualIsExpanded ? trailingVisual : null) : null

  const tooltipLabel = !visualIsExpanded ? label : undefined

  const inner = forceHardNavigation ? (
    <NavList.Item href={to} tooltipText={tooltipLabel} {...rest}>
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {label}
      {resolvedTrailingVisual !== null && resolvedTrailingVisual !== false ? (
        <NavList.TrailingVisual>{resolvedTrailingVisual}</NavList.TrailingVisual>
      ) : null}
    </NavList.Item>
  ) : (
    // `to` is consumed by `Link` (the polymorphic `as` component),
    // not by NavList.Item itself. Cast through to satisfy TS — at runtime
    // Primer forwards the prop to the rendered component. `Link`
    // matchRoutes-checks the target and hard-navigates when it isn't a
    // registered route in the current router.
    <NavList.Item
      as={Link}
      {...({to} as unknown as ComponentProps<typeof NavList.Item>)}
      tooltipText={tooltipLabel}
      {...rest}
    >
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {label}
      {resolvedTrailingVisual !== null && resolvedTrailingVisual !== false ? (
        <NavList.TrailingVisual>{resolvedTrailingVisual}</NavList.TrailingVisual>
      ) : null}
    </NavList.Item>
  )

  return inner
}

LinkItem.displayName = 'CollapsibleNavList.LinkItem'

// ---------------------------------------------------------------------------
// Group

type HeadingLevel = Extract<
  ComponentProps<typeof ActionList.GroupHeading>['as'],
  'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
>

/** Named-scale max heights the collapsed group popover can use (Primer Overlay scale). */
export type CollapsibleNavListGroupPopoverMaxHeight = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge'
/** Named-scale max widths the collapsed group popover can use (Primer Overlay scale). */
export type CollapsibleNavListGroupPopoverMaxWidth = 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge'
export type CollapsibleNavListGroupPopoverWidth = CollapsibleNavListGroupPopoverMaxWidth
/** Scale values common to both the popover max-height and max-width scales. */
export type CollapsibleNavListGroupPopoverSize = Extract<
  CollapsibleNavListGroupPopoverMaxHeight,
  CollapsibleNavListGroupPopoverMaxWidth
>
type TrailingActionRenderProps = {close: () => void}
type TrailingAction = ReactNode | ((props: TrailingActionRenderProps) => ReactNode)
export type CollapsibleNavListGroupSection = {
  key: string
  title: string
  children: ReactNode
}

type CollapsibleNavListGroupBaseProps = Omit<ComponentProps<typeof NavList.Group>, 'hideDivider'> & {
  /**
   * Icon shown as the group's trigger in the icon-only rail when the
   * surrounding sidebar is collapsed. Providing it opts the group into the
   * "collapse to popover" behavior: instead of rendering its items inline in
   * the rail, the group becomes a single button that opens a popover listing
   * the items. Requires `title` (used as the trigger's accessible name and
   * the popover heading). Without `icon` (or `title`), the group keeps the
   * default behavior of rendering its items inline with the heading hidden.
   */
  icon?: ComponentType<{size?: number; className?: string}>
  /**
   * Max height of the collapsed-group popover before it scrolls internally.
   * Maps to Primer's `Overlay` height scale. Defaults to `'large'` (432px).
   */
  popoverMaxHeight?: CollapsibleNavListGroupPopoverMaxHeight
  /**
   * Max width of the collapsed-group popover. Use this to prevent very long
   * labels from forcing the overlay to consume the full viewport width.
   */
  popoverMaxWidth?: CollapsibleNavListGroupPopoverMaxWidth
  /** Fixed width of the collapsed-group popover. */
  popoverWidth?: CollapsibleNavListGroupPopoverWidth

  /**
   * Sets both `popoverMaxHeight` and `popoverMaxWidth` to the same value. Use this
   * when you want a single scale value to control both dimensions. If you set
   * `popoverMaxHeight` or `popoverMaxWidth` individually, those values take
   * precedence over this one.
   */
  popoverSize?: CollapsibleNavListGroupPopoverSize
  /**
   * Optional action rendered next to the heading inside the collapsed-group
   * popover. Pass either an `ActionList.GroupHeading.TrailingAction` node
   * directly (for actions that should leave the popover open), or a render
   * function that receives `{close}` and returns that node. The render-function
   * form is useful when the action should dismiss the popover before starting
   * a flow such as opening an editor.
   *
   * When `expandedHeading` is set, this action is also rendered in the expanded
   * heading; there is no popover to dismiss in that state, so the render
   * function receives a no-op `close`.
   */
  trailingAction?: TrailingAction

  /** Ref attached to the group's trigger while the sidebar is collapsed. */
  collapsedTriggerRef?: Ref<HTMLLIElement>

  /**
   * Opt into a standardized expanded-state heading that renders the group's
   * `icon` as a leading visual, the `title`, and the optional `trailingAction`
   * on a single row — mirroring the collapsed popover heading. This is opt-in:
   * without it, the expanded group keeps Primer's default plain-text title
   * heading and ignores `icon`/`trailingAction` when expanded, so existing
   * consumers are unaffected.
   */
  expandedHeading?: boolean

  /** Control below the heading, shown expanded or in the group's popover, never in the rail. */
  search?: ReactNode

  /**
   * Renders a divider before the group in both expanded and collapsed states.
   * When omitted, no divider is rendered in either state.
   */
  showDivider?: boolean

  /** Class applied to the collapsed group's popover navigation. */
  popoverClassName?: string

  headingLevel?: HeadingLevel
}

type CollapsibleNavListGroupWithoutSectionsProps = CollapsibleNavListGroupBaseProps & {
  sections?: never
}

type CollapsibleNavListGroupWithSectionsProps = Omit<
  CollapsibleNavListGroupBaseProps,
  'children' | 'trailingAction' | 'expandedHeading' | 'id' | 'aria-label' | 'icon' | 'title'
> & {
  sections: readonly CollapsibleNavListGroupSection[]
  icon: ComponentType<{size?: number; className?: string}>
  title: string
  children?: never
  trailingAction?: never
  expandedHeading?: never
  // A group-level id would repeat on every section and a group-level name would
  // override the name each section gets from its own heading.
  id?: never
  'aria-label'?: never
}

export type CollapsibleNavListGroupProps =
  | CollapsibleNavListGroupWithoutSectionsProps
  | CollapsibleNavListGroupWithSectionsProps

function flattenFragmentChildren(children: ReactNode): ReactNode[] {
  // eslint-disable-next-line @eslint-react/no-children-to-array
  return Children.toArray(children).flatMap(child => {
    if (!isValidElement(child) || child.type !== Fragment) return child
    return flattenFragmentChildren((child.props as {children?: ReactNode}).children)
  })
}

function someChildIsCurrent(children: ReactNode): boolean {
  return flattenFragmentChildren(children).some(child => {
    if (!isValidElement(child)) return false
    const current = (child.props as {'aria-current'?: unknown})['aria-current']
    return current != null && current !== false && current !== 'false'
  })
}

function Group({
  title,
  children,
  icon: Icon,
  popoverMaxHeight,
  popoverMaxWidth,
  popoverWidth,
  popoverSize,
  trailingAction,
  collapsedTriggerRef,
  expandedHeading,
  showDivider,
  popoverClassName,
  headingLevel = 'h3',
  sections,
  search,
  ...props
}: CollapsibleNavListGroupProps) {
  const {isExpanded} = useCollapsibleSidebarContext()
  const height = popoverMaxHeight ?? popoverSize ?? 'large'
  const width = popoverMaxWidth ?? popoverSize
  const divider = showDivider ? <NavList.Divider style={{display: 'block'}} /> : null

  if (sections) {
    const populatedSections = sections.filter(section => flattenFragmentChildren(section.children).length > 0)
    if (populatedSections.length === 0 && !search) return null

    const {className, ...sectionGroupProps} = props

    if (isExpanded) {
      return (
        <>
          {divider}
          {search ? (
            <NavList.Group hideDivider className={styles.collapsibleGroup}>
              <GroupSearch>{search}</GroupSearch>
            </NavList.Group>
          ) : null}
          {populatedSections.map((section, index) => (
            <NavList.Group
              key={section.key}
              {...sectionGroupProps}
              hideDivider={index === 0}
              className={clsx(styles.collapsibleGroup, className)}
            >
              <NavList.GroupHeading as={headingLevel}>{section.title}</NavList.GroupHeading>
              {section.children}
            </NavList.Group>
          ))}
        </>
      )
    }

    return (
      <>
        {divider}
        <GroupPopover
          {...props}
          title={title}
          icon={Icon}
          headingLevel={headingLevel}
          popoverMaxHeight={height}
          popoverMaxWidth={width}
          popoverWidth={popoverWidth}
          popoverClassName={popoverClassName}
          collapsedTriggerRef={collapsedTriggerRef}
          sections={populatedSections}
          search={search}
        />
      </>
    )
  }

  // Expanded: when opted in via `expandedHeading`, standardize the
  // "leading icon + title + trailing action" heading so consumers don't
  // hand-roll it. Otherwise fall back to Primer's plain title heading
  // (title + items inline), keeping existing consumers unaffected.
  if (isExpanded) {
    if (expandedHeading) {
      return (
        <>
          {divider}
          {/* Enable Primer's trailing-action slot so the optional action
           * right-aligns in the heading, mirroring the collapsed popover heading. */}
          <FeatureFlags flags={{primer_react_action_list_group_heading_trailing_action: true}}>
            <NavList.Group
              {...props}
              hideDivider
              className={clsx(styles.collapsibleGroup, styles.expandedGroup, props.className)}
            >
              <NavList.GroupHeading as={headingLevel}>
                <span className={styles.expandedGroupHeadingLabel}>
                  {Icon ? <Icon /> : null}
                  {title}
                </span>
                {typeof trailingAction === 'function' ? trailingAction({close: () => {}}) : trailingAction}
              </NavList.GroupHeading>
              <GroupSearch>{search}</GroupSearch>
              {children}
            </NavList.Group>
          </FeatureFlags>
        </>
      )
    }

    return (
      <>
        {divider}
        <NavList.Group
          {...props}
          title={title}
          hideDivider
          className={clsx(Icon && styles.collapsibleGroup, props.className)}
        >
          <GroupSearch>{search}</GroupSearch>
          {children}
        </NavList.Group>
      </>
    )
  }

  // Collapsed, opted into the popover (icon + title provided): render a single
  // trigger in the rail that opens an overlay listing the group's items.
  if (Icon && title) {
    return (
      <>
        {divider}
        <GroupPopover
          {...props}
          aria-label={undefined}
          title={title}
          icon={Icon}
          headingLevel={headingLevel}
          popoverMaxHeight={height}
          popoverMaxWidth={width}
          popoverWidth={popoverWidth}
          popoverClassName={popoverClassName}
          trailingAction={trailingAction}
          collapsedTriggerRef={collapsedTriggerRef}
          search={search}
        >
          {children}
        </GroupPopover>
      </>
    )
  }

  // Collapsed without an icon: keep the default behavior — hide the visible
  // heading but preserve an aria-label on the underlying <ul> so screen
  // readers retain the navigation group context, and keep items inline.
  return (
    <>
      {divider}
      <NavList.Group {...props} title={undefined} aria-label={title} hideDivider>
        {children}
      </NavList.Group>
    </>
  )
}

Group.displayName = 'CollapsibleNavList.Group'

const GroupSearch: React.FC<{children?: ReactNode}> = ({children}) => {
  return children ? (
    <li className={styles.search} data-component="CollapsibleNavList.GroupSearch">
      {children}
    </li>
  ) : null
}

// ---------------------------------------------------------------------------
// GroupPopover — the collapsed-rail trigger + AnchoredOverlay. Internal; only
// reached from Group when the sidebar is collapsed and an icon + title exist.

type GroupPopoverProps = HTMLAttributes<HTMLLIElement> & {
  title: string
  icon: ComponentType<{size?: number; className?: string}>
  popoverMaxHeight: CollapsibleNavListGroupPopoverMaxHeight
  popoverMaxWidth?: CollapsibleNavListGroupPopoverMaxWidth
  popoverWidth?: CollapsibleNavListGroupPopoverWidth
  popoverClassName?: string
  trailingAction?: TrailingAction
  collapsedTriggerRef?: Ref<HTMLLIElement>
  children?: ReactNode
  sections?: readonly CollapsibleNavListGroupSection[]

  headingLevel?: HeadingLevel

  search?: ReactNode
}

function GroupPopover({
  title,
  icon: Icon,
  popoverMaxHeight,
  popoverMaxWidth,
  popoverWidth,
  popoverClassName,
  trailingAction,
  collapsedTriggerRef,
  children,
  sections,
  headingLevel = 'h3',
  search,
  ...liProps
}: GroupPopoverProps) {
  const parentContext = useCollapsibleSidebarContext()
  const [open, setOpen] = useState(false)
  const overlayId = useId()
  // Stable anchor ref so AnchoredOverlay positions against the trigger and
  // treats clicks on it as "inside" rather than outside-dismiss.
  const anchorRef = useRef<HTMLLIElement>(null)
  const mergedAnchorRef = useMergedRefs(anchorRef, collapsedTriggerRef)
  const shouldRestoreFocusRef = useRef(false)

  useLayoutEffect(() => {
    if (!open && shouldRestoreFocusRef.current) {
      shouldRestoreFocusRef.current = false
      anchorRef.current?.focus()
    }
  }, [open])

  // The popover content must render as if expanded so child LinkItems show
  // their full labels instead of collapsing to icon-only tooltips. LinkItem
  // keys off visualIsExpanded, so override both flags.
  const expandedContext = useMemo(
    () => ({...parentContext, isExpanded: true, isExpansionSettled: true, visualIsExpanded: true}),
    [parentContext],
  )

  const hasCurrent = sections
    ? sections.some(section => someChildIsCurrent(section.children))
    : someChildIsCurrent(children)

  // Dismiss the popover when an item is activated. A single delegated handler
  // is more robust than cloning each child's onClick: items may be wrapped in
  // fragments or generated by utilities, which cloneElement can't reach into.
  // React events from portaled controls can bubble here even though the
  // controls are outside this list. Only dismiss for its own navigation items.
  const dismissOnActivation = (event: MouseEvent<HTMLElement>) => {
    const control = (event.target as HTMLElement).closest('a, button')
    if (
      !control ||
      !event.currentTarget.contains(control) ||
      control.closest('[data-component="GroupHeadingWrap"], [data-component="CollapsibleNavList.GroupSearch"]')
    ) {
      return
    }
    // A nested subnav toggle expands its group in place rather than navigating;
    // Primer renders it with aria-expanded. Leave the popover open so the group
    // can expand instead of dismissing on the toggle click.
    if (control.hasAttribute('aria-expanded')) return
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    setOpen(false)
  }

  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        shouldRestoreFocusRef.current = true
        setOpen(false)
      }}
      anchorRef={anchorRef}
      focusZoneSettings={{disabled: true}}
      side="outside-right"
      align="start"
      overlayProps={{
        id: overlayId,
        maxHeight: popoverMaxHeight,
        maxWidth: popoverMaxWidth,
        width: popoverWidth,
        overflow: 'auto',
      }}
      renderAnchor={anchorProps => (
        // ActionList.Item, not LinkItem: it inherits the rail's icon-only
        // collapse and the selected pill (data-active) when a child is current,
        // and keeps a stable internal button. LinkItem's private wrapper churns
        // identity and loops AnchoredOverlay's overlay ref.
        <ActionList.Item
          // Forward group-level props (className, data-*). Omit `role`:
          // AnchoredOverlay types it wider than ActionList.Item allows.
          {...(liProps as Omit<typeof liProps, 'role'>)}
          {...(anchorProps as Omit<typeof anchorProps, 'role'>)}
          // styles.trigger draws the submenu caret; merge any group className.
          className={clsx(styles.trigger, liProps.className)}
          ref={mergedAnchorRef}
          active={hasCurrent}
          // The visible title below names the trigger via Primer's own
          // aria-labelledby, so an explicit aria-label would be redundant.
          aria-controls={open ? overlayId : undefined}
          _PrivateTooltipText={!open ? title : undefined}
        >
          <ActionList.LeadingVisual>
            <Icon />
          </ActionList.LeadingVisual>
          {title}
        </ActionList.Item>
      )}
    >
      <CollapsibleSidebarContext value={expandedContext}>
        {sections ? (
          <NavList aria-label={title} className={clsx(styles.popover, popoverClassName)} onClick={dismissOnActivation}>
            <GroupSearch>{search}</GroupSearch>
            {sections.map((section, index) => (
              <Fragment key={section.key}>
                {index > 0 ? <NavList.Divider style={{display: 'block'}} /> : null}
                <ActionList.Group>
                  <ActionList.GroupHeading as={headingLevel}>{section.title}</ActionList.GroupHeading>
                  {section.children}
                </ActionList.Group>
              </Fragment>
            ))}
          </NavList>
        ) : (
          /* Enable Primer's trailing-action slot so the optional action
           * right-aligns in the heading. ActionList.Group avoids NavList's
           * hardcoded leading divider. */
          <FeatureFlags flags={{primer_react_action_list_group_heading_trailing_action: true}}>
            <NavList
              aria-label={title}
              className={clsx(styles.popover, popoverClassName)}
              onClick={dismissOnActivation}
            >
              <ActionList.Group>
                <ActionList.GroupHeading as={headingLevel}>
                  {title}
                  {typeof trailingAction === 'function'
                    ? trailingAction({close: () => setOpen(false)})
                    : trailingAction}
                </ActionList.GroupHeading>
                <GroupSearch>{search}</GroupSearch>
                {children}
              </ActionList.Group>
            </NavList>
          </FeatureFlags>
        )}
      </CollapsibleSidebarContext>
    </AnchoredOverlay>
  )
}

GroupPopover.displayName = 'CollapsibleNavList.GroupPopover'

// ---------------------------------------------------------------------------
// GroupHeading — Some consumers use NavList.GroupHeading directly (instead
// of the Group title prop). Mirror the same hide-when-condensed behavior.

export type CollapsibleNavListGroupHeadingProps = ComponentProps<typeof NavList.GroupHeading>

function GroupHeading(props: CollapsibleNavListGroupHeadingProps) {
  const {isExpanded} = useCollapsibleSidebarContext()
  if (!isExpanded) return <NavList.GroupHeading {...props} hidden />
  return <NavList.GroupHeading {...props} />
}

GroupHeading.displayName = 'CollapsibleNavList.GroupHeading'
;(GroupHeading as typeof GroupHeading & {__SLOT__?: symbol}).__SLOT__ = NavList.GroupHeading.__SLOT__

export type CollapsibleNavListTrailingActionProps = ComponentProps<typeof NavList.TrailingAction>

const TrailingAction = (({tooltipDirection = 'e', ...props}: CollapsibleNavListTrailingActionProps) => {
  return <NavList.TrailingAction tooltipDirection={tooltipDirection} {...props} />
}) as typeof NavList.TrailingAction

TrailingAction.displayName = 'CollapsibleNavList.TrailingAction'
;(TrailingAction as typeof TrailingAction & {__SLOT__?: symbol}).__SLOT__ = NavList.TrailingAction.__SLOT__

// ---------------------------------------------------------------------------
// Item — low-level escape hatch over Primer's NavList.Item. Renders unchanged
// when expanded; when collapsed, hides itself if it has no LeadingVisual,
// since the rail clips the label and the item would show as a blank row.

export type CollapsibleNavListItemProps = ComponentProps<typeof NavList.Item> & {children?: ReactNode}

/** True when the item's children include a NavList.LeadingVisual slot. */
function hasLeadingVisual(children: ReactNode): boolean {
  // eslint-disable-next-line @eslint-react/no-children-to-array
  return Children.toArray(children).some(child => isValidElement(child) && child.type === NavList.LeadingVisual)
}

function Item({children, ...props}: CollapsibleNavListItemProps) {
  const {isExpanded} = useCollapsibleSidebarContext()
  if (!isExpanded && !hasLeadingVisual(children)) return null
  return <NavList.Item {...props}>{children}</NavList.Item>
}

Item.displayName = 'CollapsibleNavList.Item'

// ---------------------------------------------------------------------------
// Compound export — Root + subcomponents.
//
// We rebind a fresh function that wraps Primer's NavList so we can attach
// our own subcomponents *without mutating* the original `NavList` object
// (which other code in the app imports from `@primer/react` and would
// otherwise be affected by `Object.assign`).

function CollapsibleNavListRoot(props: ComponentProps<typeof NavList>) {
  return <NavList {...props} />
}
CollapsibleNavListRoot.displayName = 'CollapsibleNavList'

export const CollapsibleNavList = Object.assign(CollapsibleNavListRoot, {
  LinkItem,
  Group,
  GroupHeading,
  // `Item` and `TrailingAction` adapt Primer behavior; the remaining
  // subcomponents are direct pass-throughs.
  Item,
  LeadingVisual: NavList.LeadingVisual,
  TrailingVisual: NavList.TrailingVisual,
  TrailingAction,
  Divider: NavList.Divider,
  SubNav: NavList.SubNav,
  GroupExpand: NavList.GroupExpand,
  Description: NavList.Description,
})

// Re-export sub-components as named exports so react-docgen-typescript can
// extract their prop types when referenced via `<ArgTypes of={...} />` in
// MDX docs. The compound-component pattern (Object.assign) hides these from
// the docgen plugin, which only walks top-level module exports.
export {CollapsibleNavListRoot}
export {LinkItem as CollapsibleNavListLinkItem}
export {Group as CollapsibleNavListGroup}
export {GroupHeading as CollapsibleNavListGroupHeading}
export {TrailingAction as CollapsibleNavListTrailingAction}
